import { TextInput } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { RevisionFieldProps } from 'components/forms/revision/fill-out/types';
import { IFormInputSpecValueMap } from 'pages/revisions-module/template-editor/editors/form/types';

/**
 * Field used to display the mapped value of another input.
 */
export default function RevisionFieldValueMap({ spec, name, deviceId }: RevisionFieldProps<IFormInputSpecValueMap>) {
  const { form, findInputByName } = useFillOutRevisionDataProvider();
  const sourceInput = useMemo(() => findInputByName(spec.sourceInput), [findInputByName, spec.sourceInput]);
  const inputProps = form.getInputProps(name);

  let sourceValue: unknown;

  if (sourceInput?.context === 'revision') {
    sourceValue = form.values.fields[spec.sourceInput];
  } else if (sourceInput?.context === 'device' && deviceId !== undefined) {
    sourceValue = form.values.devices[String(deviceId)]?.fields?.[spec.sourceInput];
  }

  const normalizedSource = sourceValue === undefined || sourceValue === null ? undefined : String(sourceValue);
  const match =
    normalizedSource !== undefined ? spec.mappings?.find(({ source }) => source === normalizedSource) : undefined;
  const resolvedValue = match?.target ?? spec.defaultValue ?? '';

  useEffect(() => {
    const currentValue = inputProps.value;

    if (currentValue !== resolvedValue) {
      form.setFieldValue(name, resolvedValue);
    }
  }, [form, name, resolvedValue, inputProps.value]);

  return (
    <TextInput
      size="lg"
      name={name}
      label={spec.label}
      required={spec.required}
      {...inputProps}
      value={resolvedValue}
      readOnly
    />
  );
}
